import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function PortalLedger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/portal/ledger`, { withCredentials: true })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-zinc-500 text-sm">Loading ledger…</div>;
  if (!data) return <div className="text-zinc-500 text-sm">No ledger available.</div>;

  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
        <BookOpen size={18} className="text-amber-400" /> Your Ledger
      </h1>

      {data.opening_balance > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-center justify-between" data-testid="opening-balance">
          <div>
            <div className="text-amber-300 text-xs font-semibold">Opening Balance (carried forward)</div>
            <div className="text-zinc-500 text-[10px] mt-0.5">As of {data.opening_balance_as_of || '—'}</div>
          </div>
          <div className="text-amber-300 font-mono font-bold text-sm">
            {fmt(data.opening_balance)} <span className="text-[10px] opacity-70">{data.opening_balance_type}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
          <div className="text-zinc-500 text-xs">Total Invoiced</div>
          <div className="text-amber-400 font-mono font-bold text-sm mt-1">{fmt(data.total_invoiced)}</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
          <div className="text-zinc-500 text-xs">Total Paid</div>
          <div className="text-green-400 font-mono font-bold text-sm mt-1">{fmt(data.total_paid)}</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
          <div className="text-zinc-500 text-xs">Balance ({data.balance_type})</div>
          <div className={`font-mono font-bold text-sm mt-1 ${data.outstanding > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {fmt(Math.abs(data.outstanding))}
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800">
            {['Date', 'Type', 'Reference', 'Debit', 'Credit', 'Balance'].map(h => (
              <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-2.5">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {(data.entries || []).map((e, i) => (
              <tr key={i} className={`border-b border-zinc-800/50 ${e.is_opening ? 'bg-amber-500/[0.04]' : ''}`}>
                <td className="px-4 py-2 text-zinc-400 text-xs font-mono">{String(e.date).slice(0, 10)}</td>
                <td className="px-4 py-2 text-zinc-200 text-xs">{e.type}</td>
                <td className="px-4 py-2 text-zinc-500 text-xs font-mono">{e.reference}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-red-300">{e.debit > 0 ? fmt(e.debit) : ''}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-green-300">{e.credit > 0 ? fmt(e.credit) : ''}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-zinc-300 font-semibold">{fmt(e.running_balance)}</td>
              </tr>
            ))}
            {(data.entries || []).length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500 text-sm">No entries yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
